<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-monitor"></i>
          <span>{{ $t('resourceManagement.modelListTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button v-if="hasPermission('sys:model:save')" type="primary" icon="el-icon-plus" @click="addModel">{{ $t("modelConfig.add") }}</el-button>
        </div>
      </div>
    </div>

    <!-- 主体布局 -->
    <div class="main-layout">
      <!-- 左侧模型类型列表 -->
      <div class="type-sidebar">
        <div class="sidebar-header">
          <i class="el-icon-menu"></i>
          <span>{{ $t('resourceManagement.modelType') }}</span>
        </div>
        <div class="type-list-wrapper">
          <div 
            v-for="type in displayModelTypes" 
            :key="type" 
            class="type-item"
            :class="{ active: activeTab === type.toLowerCase() }"
            @click="handleTypeChange(type.toLowerCase())"
          >
            <div class="type-icon">
              <i :class="getTypeIcon(type)"></i>
            </div>
            <span class="type-label">{{ getModelTypeLabel(type) }}</span>
            <i class="el-icon-arrow-right active-arrow"></i>
          </div>
        </div>
      </div>

      <!-- 右侧内容区 -->
      <div class="content-wrapper">
        <!-- 筛选模块 -->
        <div class="control-panel has-query">
          <div class="control-container">
            <el-form :inline="true" class="filter-form" @submit.native.prevent>
              <el-form-item>
                <el-input
                  :placeholder="$t('modelConfig.searchPlaceholder')"
                  v-model="search"
                  class="filter-input"
                  clearable
                  @keyup.enter.native="handleSearch"
                >
                  <i slot="prefix" class="el-input__icon el-icon-search"></i>
                </el-input>
              </el-form-item>
              <el-form-item>
                <el-select 
                  v-model="isActivated" 
                  :placeholder="$t('modelConfig.activationStatus')" 
                  class="filter-select-small"
                  clearable
                  @change="handleSearch"
                >
                  <i slot="prefix" class="el-input__icon el-icon-tickets"></i>
                  <el-option :label="$t('modelConfig.activated')" :value="1"></el-option>
                  <el-option :label="$t('modelConfig.unactivated')" :value="0"></el-option>
                </el-select>
              </el-form-item>
              <div class="search-btns">
                <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('modelConfig.search') }}</el-button>
                <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
              </div>
            </el-form>
          </div>
        </div>

        <!-- 主体表格内容 -->
        <div class="main-content">
          <!-- 右侧表格卡片 -->
          <el-card class="list-card" shadow="never">
            <el-table
              ref="modelTable"
              v-loading="loading"
              :data="modelList"
              style="width: 100%"
              height="100%"
              class="custom-table"
              @selection-change="handleSelectionChange"
            >
          <el-table-column
            type="selection"
            width="55"
            align="center"
          ></el-table-column>
          
          <el-table-column
            :label="$t('modelConfig.modelId')"
            prop="id"
            align="center"
            min-width="120"
          ></el-table-column>

          <el-table-column
            :label="$t('modelConfig.modelName')"
            prop="modelName"
            align="center"
            min-width="180"
          >
            <template slot-scope="scope">
              <div class="name-wrapper">
                <span class="name-link" @click="viewDetail(scope.row)">{{ scope.row.modelName }}</span>
                <el-tooltip effect="dark" placement="top" v-if="isModelActivated(scope.row)">
                  <div slot="content">
                    {{ $t('modelConfig.activationDate') }}: {{ scope.row.activationDate | formatDate }}<br/>
                    {{ $t('modelConfig.expirationDate') }}: {{ scope.row.expirationDate | formatDate }}
                  </div>
                  <el-tag size="mini" type="success" class="status-tag">
                    <i class="el-icon-check"></i> {{ $t('modelConfig.activated') }}
                  </el-tag>
                </el-tooltip>
                <el-tag v-else size="mini" type="info" class="status-tag">
                  <i class="el-icon-lock"></i> {{ $t('modelConfig.unactivated') }}
                </el-tag>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('modelConfig.provider')" align="center" min-width="120">
            <template slot-scope="scope">
              <el-tag size="small" effect="plain" class="provider-tag">
                {{ scope.row.provideName || (scope.row.configJson && scope.row.configJson.type) || $t("modelConfig.unknown") }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column :label="$t('modelConfig.isEnabled')" align="center" width="100">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.isEnabled"
                :active-value="1"
                :inactive-value="0"
                :disabled="!hasPermission('sys:model:update')"
                @change="handleStatusChange(scope.row)"
              />
            </template>
          </el-table-column>

          <el-table-column :label="$t('modelConfig.isDefault')" align="center" width="100">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.isDefault"
                :active-value="1"
                :inactive-value="0"
                :disabled="!hasPermission('sys:model:update')"
                @change="handleDefaultChange(scope.row)"
              />
            </template>
          </el-table-column>

          <el-table-column
            v-if="activeTab === 'tts'"
            :label="$t('modelConfig.voiceManagement')"
            align="center"
            width="120"
          >
            <template slot-scope="scope">
              <el-button
                v-if="hasPermission('sys:model:info')"
                type="text"
                size="mini"
                icon="el-icon-microphone"
                class="voice-btn"
                @click="openTtsDialog(scope.row)"
              >
                {{ $t("modelConfig.voiceManagement") }}
              </el-button>
            </template>
          </el-table-column>

          <el-table-column
            v-if="hasAnyActionPermission"
            :label="$t('modelConfig.action')"
            align="center"
            :width="actionColumnWidth"
            fixed="right"
          >
            <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                v-if="hasPermission('sys:model:info')"
                type="primary"
                size="mini"
                plain
                icon="el-icon-view"
                @click="viewDetail(scope.row)"
              >
                {{ $t("common.detail") }}
              </el-button>
              <el-button
                v-if="hasPermission('sys:model:update')"
                type="warning"
                size="mini"
                  plain
                  icon="el-icon-edit"
                  @click="editModel(scope.row)"
                >
                  {{ $t("modelConfig.edit") }}
                </el-button>
                <el-button
                  v-if="hasPermission('sys:model:save')"
                  type="success"
                  size="mini"
                  plain
                  icon="el-icon-document-copy"
                  @click="duplicateModel(scope.row)"
                >
                  {{ $t("modelConfig.duplicate") }}
                </el-button>
                <el-button
                  v-if="hasPermission('sys:model:delete')"
                  type="danger"
                  size="mini"
                  plain
                  icon="el-icon-delete"
                  @click="deleteModel(scope.row)"
                >
                  {{ $t("modelConfig.delete") }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <div class="left-actions">
            <el-button
              v-if="hasPermission('sys:model:delete')"
              type="danger"
              size="mini"
              plain
              icon="el-icon-delete"
              :disabled="selectedModels.length === 0"
              @click="batchDelete"
            >
              {{ $t("modelConfig.batchDelete") || $t("common.batchDelete") }}
            </el-button>
          </div>
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          >
          </el-pagination>
        </div>
      </el-card>
        </div>
      </div>
    </div>

    <!-- 弹窗组件 -->
    <TtsModel
      :visible.sync="ttsDialogVisible"
      :ttsModelId="selectedTtsModelId"
      :modelConfig="selectedModelConfig"
    />

    <!-- 额度信息弹窗 -->
    <el-dialog
      :title="$t('modelConfig.quotaInfo')"
      :visible.sync="quotaDialogVisible"
      width="500px"
      append-to-body
      custom-class="quota-dialog"
    >
      <div v-loading="quotaLoading" class="quota-content">
        <template v-if="quotaData">
          <div class="quota-item">
            <span class="label">{{ $t('modelConfig.modelName') }}:</span>
            <span class="value">{{ quotaData.modelName }}</span>
          </div>
          <div class="quota-item">
            <span class="label">{{ $t('modelConfig.activationStatus') }}:</span>
            <el-tag :type="quotaData.isActivated ? 'success' : 'danger'" size="small">
              {{ quotaData.statusText }}
            </el-tag>
          </div>
          <div class="quota-item">
            <span class="label">{{ $t('modelConfig.totalQuota') }}:</span>
            <span class="value">{{ quotaData.totalQuota }}</span>
          </div>
          <div class="quota-item">
            <span class="label">{{ $t('modelConfig.usedQuota') }}:</span>
            <span class="value">{{ quotaData.usedQuota }}</span>
          </div>
          <div class="quota-item">
            <span class="label">{{ $t('modelConfig.remainingQuota') }}:</span>
            <span class="value font-bold text-primary">{{ quotaData.remainingQuota }}</span>
          </div>
          <div v-if="quotaData.usageStats" class="quota-stats">
            <div v-for="(val, key) in quotaData.usageStats" :key="key" class="quota-item">
              <span class="label">{{ key }}:</span>
              <span class="value">{{ val }}</span>
            </div>
          </div>
          <div v-if="quotaData.configJson" class="config-json-section">
            <div class="label">{{ $t('modelConfig.configParams') || '配置参数' }}:</div>
            <pre class="config-json-content">{{ formatJson(quotaData.configJson) }}</pre>
          </div>
          <div class="refresh-time">
            {{ $t('common.updatedAt') }}: {{ quotaData.refreshTime | formatDateTime }}
          </div>
        </template>
        <el-empty v-else-if="!quotaLoading" :description="$t('common.noData')"></el-empty>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="quotaDialogVisible = false">{{ $t('common.close') }}</el-button>
        <el-button type="primary" icon="el-icon-refresh" :loading="quotaLoading" @click="refreshQuota">
          {{ $t('modelConfig.refreshQuota') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import TtsModel from "./TtsModel.vue"
import { hasPermission } from '@/utils/permission'

export default {
  name: 'ModelList',
  components: { TtsModel },
  data() {
    const query = this.$route.query
    return {
      activeTab: (query.type || "llm").toLowerCase(),
      search: query.search || "",
      isActivated: query.isActivated !== undefined ? Number(query.isActivated) : undefined,
      currentPage: query.page ? Number(query.page) : 1,
      pageSize: query.pageSize ? Number(query.pageSize) : 10,
      selectedModels: [],
      ttsDialogVisible: false,
      selectedTtsModelId: "",
      selectedModelConfig: {},
      quotaDialogVisible: false,
      quotaLoading: false,
      quotaData: null,
      currentModelId: ""
    }
  },
  filters: {
    formatDate(val) {
      if (!val) return '-'
      const date = new Date(val)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    formatDateTime(val) {
      if (!val) return '-'
      const date = new Date(val)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    }
  },
  computed: {
    ...mapState('model', ['modelList', 'total', 'loading', 'modelTypes']),
    modelTypeText() {
      // 如果 activeTab 是小写（如 'llm'），需要转换为对应的 i18n key（如 'llmModel'）
      const type = this.activeTab.toUpperCase()
      return this.getModelTypeLabel(type) || this.$t("modelConfig.modelConfig")
    },
    displayModelTypes() {
      // 确保至少显示常用的几种类型，防止接口返回空导致下拉框无数据
      // 顺序参考 agent-console 的 ModelConfig.vue
      const defaultTypes = ['VAD', 'ASR', 'LLM', 'VLLM', 'INTENT', 'TTS', 'MEMORY', 'RAG']
      if (!this.modelTypes || this.modelTypes.length === 0) {
        return defaultTypes
      }
      return this.modelTypes
    },
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:model:info')) count += 1
      if (this.hasPermission('sys:model:update')) count += 1
      if (this.hasPermission('sys:model:save')) count += 1
      if (this.hasPermission('sys:model:delete')) count += 1
      // TTS 语音管理按钮
      if (this.activeTab === 'tts' && this.hasPermission('sys:model:info')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 90, 2: 155, 3: 220, 4: 280, 5: 350 }
      return widthMap[count] || count * 70
    }
  },
  created() {
    this.initData()
  },
  methods: {
    hasPermission,
    ...mapActions('model', [
      'fetchModelTypes',
      'fetchModelList',
      'saveModelConfig',
      'deleteModelConfig',
      'toggleModelStatus',
      'toggleActivationStatus',
      'setAsDefault',
      'fetchModelQuota'
    ]),
    getModelTypeLabel(type) {
      if (!type) return ''
      const typeLower = type.toLowerCase()
      const label = this.$t(`modelConfig.${typeLower}`)
      // 如果翻译后的文本不等于 key 本身，说明找到了翻译
      return (label && label !== `modelConfig.${typeLower}`) ? label : type.toUpperCase()
    },
    getTypeIcon(type) {
      const icons = {
        'LLM': 'el-icon-chat-dot-round',
        'VLLM': 'el-icon-video-camera',
        'ASR': 'el-icon-microphone',
        'TTS': 'el-icon-headset',
        'VAD': 'el-icon-odometer',
        'INTENT': 'el-icon-aim',
        'MEMORY': 'el-icon-notebook-1',
        'RAG': 'el-icon-search'
      }
      return icons[type.toUpperCase()] || 'el-icon-collection-tag'
    },
    async initData() {
      await this.fetchModelTypes()
      // 移除这里的冗余赋值，因为已经在 data() 中根据 query 初始化了
      this.loadData()
    },
    loadData() {
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        modelType: this.activeTab.toUpperCase(),
        name: this.search,
        isActivated: this.isActivated
      }
      this.fetchModelList(params)
    },
    handleTypeChange(val) {
      this.activeTab = val
      this.currentPage = 1
      this.loadData()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.search = ""
      this.isActivated = undefined
      this.currentPage = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadData()
    },
    handleSelectionChange(val) {
      this.selectedModels = val
    },
    addModel() {
      this.$router.push({
        name: 'ModelAdd',
        params: { type: this.activeTab.toUpperCase() },
        query: { page: this.currentPage, pageSize: this.pageSize, search: this.search, isActivated: this.isActivated }
      })
    },
    editModel(model) {
      this.$router.push({
        name: 'ModelEdit',
        params: { id: model.id, type: this.activeTab.toUpperCase() },
        query: { page: this.currentPage, pageSize: this.pageSize, search: this.search, isActivated: this.isActivated }
      })
    },
    duplicateModel(model) {
      this.$router.push({
        name: 'ModelEdit',
        params: { id: model.id, type: this.activeTab.toUpperCase() },
        query: { duplicate: 'true', page: this.currentPage, pageSize: this.pageSize, search: this.search, isActivated: this.isActivated }
      })
    },
    async deleteModel(model) {
      try {
        await this.$confirm(this.$t("modelConfig.confirmDelete"), this.$t("common.warning"), {
          type: 'warning'
        })
        await this.deleteModelConfig(model.id)
        this.$message.success(this.$t("modelConfig.deleteSuccess"))
        this.loadData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t("modelConfig.deleteFailed"))
        }
      }
    },
    async batchDelete() {
      try {
        await this.$confirm(this.$t("modelConfig.confirmBatchDelete"), this.$t("common.warning"), {
          type: 'warning'
        })
        const deletePromises = this.selectedModels.map(m => this.deleteModelConfig(m.id))
        await Promise.all(deletePromises)
        this.$message.success(this.$t("modelConfig.batchDeleteSuccess"))
        this.loadData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t("modelConfig.partialDeleteFailed"))
        }
      }
    },
    async handleStatusChange(model) {
      try {
        await this.toggleModelStatus({ id: model.id, status: model.isEnabled })
        this.$message.success(model.isEnabled ? this.$t("modelConfig.enableSuccess") : this.$t("modelConfig.disableSuccess"))
        this.loadData()
      } catch (error) {
        model.isEnabled = model.isEnabled === 1 ? 0 : 1
        this.$message.error(this.$t("modelConfig.operationFailed"))
      }
    },
    async handleDefaultChange(model) {
      try {
        if (model.isDefault === 0) {
          // If trying to uncheck default, we might want to prevent it if it's the only default
          // But usually backend handles setting another one as default
        }
        await this.setAsDefault(model.id)
        this.$message.success(this.$t("modelConfig.setDefaultSuccess"))
        this.loadData()
      } catch (error) {
        model.isDefault = model.isDefault === 1 ? 0 : 1
        this.$message.error(this.$t("modelConfig.operationFailed"))
      }
    },
    openTtsDialog(row) {
      this.selectedTtsModelId = row.id.toString()
      this.selectedModelConfig = row
      this.ttsDialogVisible = true
    },
    viewDetail(model) {
      this.$router.push({
        name: 'ModelDetail',
        params: { id: model.id, type: this.activeTab.toUpperCase() },
        query: { page: this.currentPage, pageSize: this.pageSize, search: this.search, isActivated: this.isActivated }
      })
    },
    isModelActivated(row) {
      if (row.isActivated === 1) return true;
      
      // 检查 configJson 中是否包含有效的 key
      if (row.configJson) {
        const config = typeof row.configJson === 'string' ? JSON.parse(row.configJson) : row.configJson;
        const keyFields = ['api_key', 'apiKey', 'key', 'access_token', 'token'];
        for (const field of keyFields) {
          const val = config[field];
          // 有效 Key 判断：非空、长度 >= 20、且不包含占位符“你的”
          if (val && typeof val === 'string' && val.trim().length >= 20 && !val.includes('你的')) {
            return true;
          }
        }
      }
      return false;
    },
    async viewQuota(row) {
      // 检查是否有有效的 API Key
      if (!this.isModelActivated(row)) {
        this.$message.warning(this.$t('modelConfig.checkKeyFirst'));
        return;
      }

      this.currentModelId = row.id.toString()
      this.quotaDialogVisible = true
      this.quotaData = null
      await this.refreshQuota(false)
    },
    async refreshQuota(refresh = true) {
      if (!this.currentModelId) return
      
      this.quotaLoading = true
      try {
        const res = await this.fetchModelQuota({ id: this.currentModelId, refresh })
        if (res) {
          this.quotaData = res
        } else {
          this.$message.warning(this.$t('modelConfig.notSupported') || '该模型暂不支持额度查询')
          this.quotaDialogVisible = false
        }
      } catch (error) {
        console.error('Fetch quota error:', error)
        this.$message.error(this.$t('common.operationFailed'))
      } finally {
        this.quotaLoading = false
      }
    },
    formatJson(json) {
      if (!json) return ''
      try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return json
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 模型管理页面特有样式 */
.list-page-container {
  .filter-form {
    .filter-input { width: 220px; }
    .filter-select-small { width: 140px; }
  }

  .name-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .name-link {
      font-weight: 600;
      color: #409eff;
      cursor: pointer;
      &:hover {
        color: #66b1ff;
        text-decoration: underline;
      }
    }

    .status-tag {
      margin-left: 6px;
      border-radius: 4px;
    }
  }

  .custom-table {
    .provider-tag {
      border-radius: 4px;
      font-weight: 500;
    }
  }
}

.quota-content {
  padding: 10px 0;

  .quota-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }

    .label {
      color: #64748b;
      font-size: 14px;
    }

    .value {
      color: #1e293b;
      font-weight: 500;
      font-size: 14px;

      &.text-primary {
        color: #409eff;
      }

      &.font-bold {
        font-weight: 600;
      }
    }
  }

  .refresh-time {
    margin-top: 20px;
    text-align: right;
    font-size: 12px;
    color: #94a3b8;
  }

  .config-json-section {
    margin-top: 15px;
    border-top: 1px solid #ebeef5;
    padding-top: 15px;

    .label {
      font-size: 14px;
      color: #606266;
      margin-bottom: 8px;
      font-weight: bold;
    }

    .config-json-content {
      background-color: #f5f7fa;
      padding: 10px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      color: #409eff;
      overflow-x: auto;
      margin: 0;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }
}
</style>
